import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import getXsrfToken from '@atlassian/jira-platform-xsrf-token';
import { toErrorId, toErrorMessage } from '@atlassian/jira-project-settings-apps-common/src/types';
import { famaCatchAndLogFetchError } from '@atlassian/jira-project-settings-apps-common/src/util/catch-and-log-fetch-error';
import { logError } from '@atlassian/jira-project-settings-apps-common/src/util/error-logger';
import { PACKAGE_NAME } from '../../model/constants';
import type { AvatarDetails } from '../../model/types';
import { getProjectAvatarUrl } from '../project/get-project-details';
import type { ProjectContext } from '../project/types';
import type { AvatarService, AvatarUpdate, CroppingProperties } from './types';

/**
 * returns the end point url based on passed project context
 */
const getEndPointUrl = (context: ProjectContext, croppingProperties: CroppingProperties): string =>
	`${context.baseUrl}/rest/api/2/universal_avatar/type/project/owner/${context.projectId}` +
	// we are getting XSRF token from cookies directly because it is not being in the store and can be outdated
	`?atl_token=${getXsrfToken() || ''}` +
	`&x=${Math.floor(croppingProperties.x)}` +
	`&y=${Math.floor(croppingProperties.y)}` +
	`&size=${Math.floor(croppingProperties.size)}`;

/**
 * Transforms data received from REST endpoint to ProjectDetails object
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const transformAvatarDetailsFromServer = (context: ProjectContext, json: any): AvatarDetails => ({
	id: parseInt(json.id, 10),
	avatarUrl: getProjectAvatarUrl(context, json.id),
});

/**
 * Service to manage uploading project avatar
 */
const AvatarServiceImpl: AvatarService = {
	uploadAvatar: (context: ProjectContext, update: AvatarUpdate): Promise<AvatarDetails> =>
		famaCatchAndLogFetchError(
			performPostRequest(getEndPointUrl(context, update.croppingProperties), {
				body: update.file,
				headers: {},
			}),
			PACKAGE_NAME,
			toErrorId('uploadProjectAvatar'),
			toErrorMessage('Failed to upload an avatar for project details'),
		).then((result) => {
			try {
				return transformAvatarDetailsFromServer(context, result);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				logError(
					PACKAGE_NAME,
					toErrorId('transformProjectAvatar'),
					toErrorMessage('Failed to transform an uploaded avatar for project details'),
					error,
				);
				throw error;
			}
		}),
};

export default AvatarServiceImpl;
